import { inject, Injectable } from "@angular/core";
import { MicroBilling } from "../../../pojo/Pricing";
import { EventPricingService } from "../reference-set-pricing/s25.event.pricing.service";
import { ORG_INVOICE_CACHE } from "./org-invoice-cache";

/**
 * What the email compose panel reads its billing through when it is opened from the Invoices tab.
 *
 * The panel is the event pricing sidebar, scoped to one event's billing and given nothing but an
 * event id and an invoice id - so its `PricingStore` reads `billingCustom.json` for an invoice the
 * tab already fetched to draw the row. Provided on the panel's host, this answers that first read
 * out of the tab's cache instead. The event pricing page is untouched: it keeps the root service.
 */
@Injectable()
export class OrgSeededPricingService extends EventPricingService {
    private readonly cache = inject(ORG_INVOICE_CACHE);

    /**
     * The event sets already answered from the cache. A repeat is a reload - which is what the
     * store does after a payment is logged - and has to reach the server, since a payment changes
     * balances the cached copy still describes the old way.
     */
    private readonly seeded = new Set<string>();

    override async getInvoices(eventIds: number[]): Promise<MicroBilling> {
        const key = [...eventIds].sort((first, second) => first - second).join(" ");

        if (!this.seeded.has(key)) {
            const cached = this.cache.billingFor(eventIds);
            if (cached) {
                this.seeded.add(key);
                return cached;
            }
        }

        const billing = await super.getInvoices(eventIds);
        this.cache.record(eventIds, billing);
        return billing;
    }
}
